/* The code runner UI (02 §3.3.4): task pane, editor, Run, output/check panel,
 * Reset. Embedded inside the drill and Quiz players; standalone and ungraded
 * inside a lesson.
 *
 * Two rules here are easy to fake and are implemented honestly:
 *
 *  1. Run is repeatable without penalty. Attempts are counted and reported in
 *     the outcome event, but nothing about a second Run scores worse than the
 *     first (02 §2.8).
 *  2. Check SOURCE is never rendered — only each check's name, verdict and
 *     message. `checks_py` reaches the device because the device is what runs
 *     it; it is passed to the worker and never to a DOM node (M4-4).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CODE_RUN_TIMEOUT_MS } from '@ascent/shared';
import { Markdown } from '../markdown';
import { checkReading, describeSql, runPython, runSql } from './runner';
import { formatBytes, packState } from './pack';
import type { RunOutcome, TablePreview } from './types';

export type ExerciseSpec =
  | { type: 'python'; starter_code: string; checks_py: string }
  | { type: 'sql'; seed_sql: string; expected_rows: unknown[][]; order_sensitive: boolean }
  | { type: 'reading'; code_md: string; answer_mode: 'exact'; accepted_answers: string[] };

interface Props {
  exercise: ExerciseSpec;
  /** Quiz items disable Run until confidence is set (02 §2.8). */
  runDisabled?: boolean | undefined;
  runDisabledHint?: string | undefined;
  /** Fires after every Run — the caller decides what, if anything, to record. */
  onOutcome?: ((outcome: RunOutcome, attempts: number) => void) | undefined;
  onOpenSettings?: (() => void) | undefined;
}

const PLACEHOLDER: Record<string, string> = {
  python: '# your code here',
  sql: '-- your query here',
  reading: '',
};

function starterFor(ex: ExerciseSpec): string {
  return ex.type === 'python' ? ex.starter_code : '';
}

export function CodeRunner({ exercise, runDisabled, runDisabledHint, onOutcome, onOpenSettings }: Props) {
  const [source, setSource] = useState(() => starterFor(exercise));
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [packSize, setPackSize] = useState<string | null>(null);
  const [tables, setTables] = useState<TablePreview[] | null>(null);

  /* Reset when the exercise changes — a stale editor buffer carried into the
   * next Card is the kind of bug that silently scores the wrong thing.
   *
   * Keyed on a VALUE signature, not on the prop's identity. A caller that builds
   * its exercise object inline hands us a new object on every render, and an
   * identity-keyed effect then fires on the re-render caused by the result
   * itself — wiping the checks the moment they arrive, with no error anywhere.
   * (It did exactly that, and the Run button quietly returned to "Run".) */
  const signature = useMemo(
    () =>
      exercise.type === 'python'
        ? `python:${exercise.starter_code}:${exercise.checks_py.length}`
        : exercise.type === 'sql'
          ? `sql:${exercise.seed_sql}`
          : `reading:${exercise.code_md}`,
    [exercise],
  );

  useEffect(() => {
    setSource(starterFor(exercise));
    setOutcome(null);
    setAttempts(0);
    setRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value signature, by design
  }, [signature]);

  useEffect(() => {
    if (exercise.type !== 'python') return;
    void packState().then((s) => setPackSize(formatBytes(s.totalBytes || 0)));
  }, [exercise.type]);

  /* Show the seeded database BEFORE anything is typed. A `sql` task is "task +
   * read-only seeded database" (02 §2.8); rendering only the prose left the
   * question unanswerable — you cannot query a `status` column you have never
   * been told exists. */
  useEffect(() => {
    if (exercise.type !== 'sql') { setTables(null); return; }
    let live = true;
    setTables(null);
    void describeSql(exercise.seed_sql).then((t) => { if (live) setTables(t); });
    return () => { live = false; };
  }, [signature, exercise]);

  const run = useCallback(async () => {
    if (running || runDisabled) return;
    setRunning(true);
    try {
      const result =
        exercise.type === 'python'
          ? await runPython(source, exercise.checks_py)
          : exercise.type === 'sql'
            ? await runSql(source, exercise.seed_sql, exercise.expected_rows, exercise.order_sensitive)
            : checkReading(source, exercise.accepted_answers);

      const n = attempts + 1;
      setOutcome(result);
      setAttempts(n);
      onOutcome?.(result, n);
    } finally {
      setRunning(false);
    }
  }, [running, runDisabled, exercise, source, attempts, onOutcome]);

  const blocked = outcome?.status === 'blocked';

  return (
    <div className="runner">
      {exercise.type === 'reading' ? (
        <div className="runner__code">
          <Markdown source={exercise.code_md} />
        </div>
      ) : null}

      {/* The database you are querying — schema and seeded rows, read back from
        * the engine itself so it cannot drift from what the query will see. */}
      {exercise.type === 'sql' ? (
        tables === null ? (
          <p className="hint">Loading the database…</p>
        ) : tables.length === 0 ? (
          <p className="hint">This exercise's database could not be loaded — that is a content bug, not your query.</p>
        ) : (
          <div className="schema">
            <p className="runner__label">The database (read-only)</p>
            {tables.map((t) => (
              <div className="schema__table" key={t.name}>
                <p className="schema__name">
                  {t.name} <span className="schema__cols">({t.columns.join(', ')})</span>
                </p>
                <div className="runner__grid" role="table" aria-label={`Table ${t.name}`}>
                  <div className="runner__gridrow runner__gridrow--head" role="row">
                    {t.columns.map((c) => <span key={c} role="columnheader">{c}</span>)}
                  </div>
                  {t.rows.map((row, i) => (
                    <div className="runner__gridrow" role="row" key={i}>
                      {row.map((v, j) => (
                        <span key={j} role="cell">{v === null ? '∅' : String(v)}</span>
                      ))}
                    </div>
                  ))}
                </div>
                {t.more > 0 ? <p className="hint">…and {t.more} more rows.</p> : null}
              </div>
            ))}
          </div>
        )
      ) : null}

      <label className="runner__label" htmlFor="runner-src">
        {exercise.type === 'reading' ? 'Your answer' : exercise.type === 'sql' ? 'SQL' : 'Python'}
      </label>
      <textarea
        id="runner-src"
        className={`runner__editor ${exercise.type === 'reading' ? 'runner__editor--short' : ''}`}
        value={source}
        onChange={(e) => setSource(e.target.value)}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        placeholder={PLACEHOLDER[exercise.type]}
        rows={exercise.type === 'reading' ? 2 : 10}
      />

      <div className="runner__actions">
        <button type="button" onClick={() => void run()} disabled={running || runDisabled}>
          {running ? 'Running…' : attempts > 0 ? 'Run again' : 'Run'}
        </button>
        {exercise.type !== 'reading' ? (
          <button
            type="button"
            className="runner__reset"
            onClick={() => { setSource(starterFor(exercise)); setOutcome(null); }}
            disabled={running}
          >
            Reset
          </button>
        ) : null}
      </div>

      {runDisabled && runDisabledHint ? <p className="hint">{runDisabledHint}</p> : null}
      {attempts > 1 && !running ? (
        <p className="hint">Attempt {attempts} — running again costs nothing.</p>
      ) : null}

      {/* ---- Blocked: offline, no pack (02 §6.10) ------------------------- */}
      {blocked ? (
        <div className="card card--blocked">
          <div className="card__title"><span>Python needs the offline pack</span></div>
          <p style={{ margin: '0 0 0.875rem' }}>
            You are offline and the Python runtime is not on this device yet.
            The pack is {packSize ?? 'about 13 MB'} — Wi-Fi recommended.
          </p>
          {onOpenSettings ? (
            <button type="button" onClick={onOpenSettings}>Open Settings</button>
          ) : (
            <p className="hint">Settings → Offline pack, once you are back online.</p>
          )}
        </div>
      ) : null}

      {/* ---- Output ------------------------------------------------------- */}
      {outcome && outcome.status === 'timeout' ? (
        <div className="card card--blocked">
          <div className="card__title"><span>Stopped after {CODE_RUN_TIMEOUT_MS / 1000} seconds</span></div>
          <p style={{ margin: 0 }}>
            That looks like an endless loop. Nothing is broken and nothing is
            recorded against you — fix the loop and run it again.
          </p>
        </div>
      ) : null}

      {outcome && outcome.status === 'error' ? (
        <div className="runner__out">
          <p className="runner__verdict runner__verdict--no">Error</p>
          {outcome.stdout ? <pre className="runner__stdout">{outcome.stdout}</pre> : null}
          <pre className="runner__error">{outcome.message}</pre>
        </div>
      ) : null}

      {outcome && outcome.status === 'ok' ? (
        <div className="runner__out">
          <p className={`runner__verdict ${outcome.passed ? 'runner__verdict--ok' : 'runner__verdict--no'}`}>
            {outcome.passed ? 'All checks passed' : 'Not yet'}
          </p>

          {outcome.stdout ? <pre className="runner__stdout">{outcome.stdout}</pre> : null}

          {outcome.rows ? (
            <div className="runner__grid" role="table" aria-label="Result">
              <div className="runner__gridrow runner__gridrow--head" role="row">
                {(outcome.columns ?? []).map((c) => (
                  <span key={c} role="columnheader">{c}</span>
                ))}
              </div>
              {outcome.rows.slice(0, 20).map((row, i) => (
                <div className="runner__gridrow" role="row" key={i}>
                  {row.map((v, j) => (
                    <span key={j} role="cell">{v === null ? '∅' : String(v)}</span>
                  ))}
                </div>
              ))}
              {outcome.rows.length > 20 ? (
                <p className="hint">…and {outcome.rows.length - 20} more rows.</p>
              ) : null}
            </div>
          ) : null}

          <ul className="checks">
            {outcome.checks.map((c, i) => (
              <li key={`${c.name}-${i}`} className={c.passed ? 'checks--ok' : 'checks--no'}>
                <span className="checks__mark">{c.passed ? 'pass' : 'fail'}</span>
                <span className="checks__name">{c.name}</span>
                <span className="checks__msg">{c.message}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
